import { NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useList } from "@/pages/admin/ui";
import { IMG } from "@/lib/images";
import {
  LayoutDashboard, Inbox, CalendarClock, Repeat, UtensilsCrossed, Building,
  MapPin, Users, Video, Sparkles, Settings, LogOut, ShieldCheck, ExternalLink, Gift, Images, History,
} from "lucide-react";

const NAV = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/enquiries", label: "Enquiries", icon: Inbox },
  { to: "/admin/events", label: "Event Pipeline", icon: CalendarClock },
  { to: "/admin/bulk", label: "Bulk & Contracts", icon: Repeat },
  { to: "/admin/menu", label: "Menu Management", icon: UtensilsCrossed },
  { to: "/admin/packages", label: "Menu Packages", icon: Gift },
  { to: "/admin/gallery", label: "Gallery", icon: Images },
  { to: "/admin/brands", label: "Brands", icon: Building },
  { to: "/admin/branches", label: "Branches", icon: MapPin },
  { to: "/admin/staff", label: "Staff", icon: Users },
  { to: "/admin/cctv", label: "CCTV", icon: Video },
  { to: "/admin/forecast", label: "AI Forecast", icon: Sparkles },
  { to: "/admin/activity", label: "Activity Log", icon: History },
  { to: "/admin/users", label: "Users & Roles", icon: ShieldCheck },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const { data: notifs } = useList("/admin/notifications", []);
  const unread = (notifs || []).filter((n) => !n.is_read).length;

  if (loading || user === null) return <div className="grid min-h-screen place-items-center bg-[#F8F9FA] text-[#64748B]">Loading DineOS…</div>;
  if (!user) return <Navigate to="/admin" replace />;

  const roleLabel = (user.role || "").replace(/_/g, " ");

  return (
    <div className="flex min-h-screen bg-[#F8F9FA]">
      <aside className="fixed inset-y-0 left-0 hidden w-64 flex-col bg-[#121211] text-ivory lg:flex">
        <div className="flex items-center gap-2 border-b border-white/10 px-5 py-5">
          <img src={IMG.logo} alt="Mee2Momos" className="h-10 w-11 rounded-t-full border border-gold/40 bg-ivory object-contain p-1" />
          <div><div className="font-display text-lg leading-none">Mee2Momos</div><div className="text-[9px] uppercase tracking-editorial text-gold">DineOS</div></div>
        </div>
        <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-4">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`admin-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-white/10 text-gold" : "text-white/70 hover:bg-white/5 hover:text-white"}`}>
              <n.icon className="h-4 w-4" /> {n.label}
              {n.label === "Enquiries" && unread > 0 && <span className="ml-auto rounded-full bg-gold px-1.5 text-[10px] font-bold text-charcoal">{unread}</span>}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 p-3">
          <div className="mb-2 px-2 text-xs text-white/50">{user.name}<br /><span className="text-gold capitalize">{roleLabel}</span></div>
          <button onClick={() => { logout(); nav("/admin"); }} data-testid="admin-logout" className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-white/70 hover:bg-white/5 hover:text-white">
            <LogOut className="h-4 w-4" /> Sign Out
          </button>
        </div>
      </aside>

      <div className="flex-1 lg:ml-64">
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-[#E2E8F0] bg-white px-4 py-3 lg:px-8">
          <div className="lg:hidden font-display text-lg text-espresso">DineOS</div>
          <div className="ml-auto flex items-center gap-3">
            <a href="/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-medium text-[#64748B] hover:text-[#C5A059]"><ExternalLink className="h-3.5 w-3.5" /> View Website</a>
            <div className="grid h-9 w-9 place-items-center rounded-full bg-[#2C201A] text-sm font-semibold text-white">{(user.name || "A")[0]}</div>
          </div>
        </header>
        {/* mobile nav */}
        <div className="flex gap-1 overflow-x-auto border-b border-[#E2E8F0] bg-white px-2 py-2 lg:hidden">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} className={({ isActive }) => `whitespace-nowrap rounded-md px-3 py-1.5 text-xs ${isActive ? "bg-[#2C201A] text-white" : "text-[#64748B]"}`}>{n.label}</NavLink>
          ))}
        </div>
        <main className="p-4 lg:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
